import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  Lightbulb, 
  Wrench, 
  AlertTriangle, 
  Cpu, 
  Tag, 
  FileText, 
  Zap, 
  Building2, 
  Hash, 
  ExternalLink, 
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Share2,
  Bookmark
} from "lucide-react";
import type { GlossaryEntry, AiTermInsight, SponsorshipAsset } from "@shared/schema";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";

const KEYWORD_TYPE_CONFIG: Record<string, { label: string; icon: typeof Cpu; color: string; bgColor: string }> = {
  technical_term: { label: "Technical Term", icon: Cpu, color: "text-blue-600 dark:text-blue-400", bgColor: "bg-blue-500/10" },
  standard: { label: "Standard", icon: FileText, color: "text-purple-600 dark:text-purple-400", bgColor: "bg-purple-500/10" },
  product_brand: { label: "Brand", icon: Building2, color: "text-green-600 dark:text-green-400", bgColor: "bg-green-500/10" },
  product_description: { label: "Product", icon: Tag, color: "text-orange-600 dark:text-orange-400", bgColor: "bg-orange-500/10" },
  acronym: { label: "Acronym", icon: Hash, color: "text-indigo-600 dark:text-indigo-400", bgColor: "bg-indigo-500/10" },
  concept: { label: "Concept", icon: Zap, color: "text-pink-600 dark:text-pink-400", bgColor: "bg-pink-500/10" },
};

interface ModalPayload {
  entry: GlossaryEntry;
  sponsorship: {
    id: string;
    ctaLabel: string | null;
    ctaUrl: string | null;
    ctaType: string | null;
    companyName: string | null;
    companyLogo: string | null;
    assets: SponsorshipAsset[];
  } | null;
  insights: AiTermInsight[];
  measurementToken: string;
}

export default function GlossaryTermPage() {
  const { slug } = useParams();
  const [, setLocation] = useLocation();

  const { data: entry, isLoading, error } = useQuery<GlossaryEntry>({
    queryKey: ["/api/glossary/slug", slug],
    enabled: !!slug,
  });

  const { data: modalPayload } = useQuery<ModalPayload>({
    queryKey: [`/api/glossary/${entry?.id}/modal`],
    enabled: !!entry?.id,
  });

  const { data: allEntries = [] } = useQuery<GlossaryEntry[]>({
    queryKey: ["/api/glossary"],
  });

  const parseExternalLinks = (linksJson: string | null): Array<{ label: string; url: string }> => {
    if (!linksJson) return [];
    try {
      const parsed = JSON.parse(linksJson);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(link => 
        typeof link === "object" && 
        link !== null && 
        typeof link.label === "string" && 
        typeof link.url === "string"
      );
    } catch {
      return [];
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="max-w-4xl mx-auto px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Glossary", href: "/glossary" }, { label: "Loading…" }]} />
        </div>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-4 bg-muted rounded w-2/3" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="max-w-4xl mx-auto px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Glossary", href: "/glossary" }, { label: "Term Not Found" }]} />
        </div>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Card className="p-8 text-center">
            <h1 className="text-2xl font-semibold mb-4">Term Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The glossary term you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => setLocation("/glossary")} variant="outline" data-testid="link-back-glossary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Browse All Terms
            </Button>
          </Card>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const keywordTypeConfig = entry.keywordType 
    ? KEYWORD_TYPE_CONFIG[entry.keywordType] || KEYWORD_TYPE_CONFIG.technical_term
    : KEYWORD_TYPE_CONFIG.technical_term;
  
  const KeywordIcon = keywordTypeConfig.icon;
  const sponsorship = modalPayload?.sponsorship;
  const insights = modalPayload?.insights || [];
  const externalLinks = parseExternalLinks(entry.externalLinks);

  const relatedEntries = entry.relatedTerms?.map(term => 
    allEntries.find(e => e.term.toLowerCase() === term.toLowerCase() || e.title?.toLowerCase() === term.toLowerCase())
  ).filter(Boolean) as GlossaryEntry[] || [];

  const pageTitle = `${entry.title || entry.term} - Glossary | FM World`;
  const pageDescription = entry.definition.length > 160 
    ? entry.definition.substring(0, 157) + "..."
    : entry.definition;
  const canonicalUrl = `/glossary/${entry.slug || entry.normalizedTerm}`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={entry.title || entry.term} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>
      <SiteHeader />
      <div className="max-w-4xl mx-auto px-4 pt-4 w-full">
        <PageBreadcrumb
          items={[
            { label: "Glossary", href: "/glossary" },
            { label: entry.title || entry.term },
          ]}
        />
      </div>
      <div className="container mx-auto px-4 py-4 max-w-4xl flex-1">

        <article className="space-y-8">
          <header className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge 
                variant="outline" 
                className={`flex items-center gap-1.5 px-3 py-1 ${keywordTypeConfig.color} ${keywordTypeConfig.bgColor}`}
              >
                <KeywordIcon className="h-4 w-4" />
                {keywordTypeConfig.label}
              </Badge>
              {sponsorship && (
                <Badge variant="secondary" className="flex items-center gap-1.5 bg-primary/10 text-primary">
                  <Building2 className="h-3 w-3" />
                  Sponsored by {sponsorship.companyName}
                </Badge>
              )}
            </div>

            <h1 className="text-4xl font-bold tracking-tight" data-testid="text-term-title">
              {entry.title || entry.term}
            </h1>
            
            {entry.term !== entry.title && (
              <p className="text-lg text-muted-foreground">
                Also known as: <span className="font-medium">{entry.term}</span>
              </p>
            )}
          </header>

          <section className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-xl leading-relaxed text-foreground/90" data-testid="text-definition">
              {entry.definition}
            </p>
          </section>

          {(entry.whyItMatters || entry.inPractice || entry.commonPitfalls) && (
            <section className="grid gap-6 md:grid-cols-3">
              {entry.whyItMatters && (
                <Card className="border-l-4 border-l-blue-500 bg-blue-500/5 hover-elevate">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="h-5 w-5 text-blue-500" />
                      <h3 className="font-semibold">Why It Matters</h3>
                    </div>
                    <div 
                      className="text-sm text-muted-foreground prose prose-sm dark:prose-invert max-w-none" 
                      dangerouslySetInnerHTML={{ __html: entry.whyItMatters }} 
                    />
                  </CardContent>
                </Card>
              )}
              {entry.inPractice && (
                <Card className="border-l-4 border-l-green-500 bg-green-500/5 hover-elevate">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Wrench className="h-5 w-5 text-green-500" />
                      <h3 className="font-semibold">In Practice</h3>
                    </div>
                    <div 
                      className="text-sm text-muted-foreground prose prose-sm dark:prose-invert max-w-none" 
                      dangerouslySetInnerHTML={{ __html: entry.inPractice }} 
                    />
                  </CardContent>
                </Card>
              )}
              {entry.commonPitfalls && (
                <Card className="border-l-4 border-l-amber-500 bg-amber-500/5 hover-elevate">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      <h3 className="font-semibold">Common Pitfalls</h3>
                    </div>
                    <div 
                      className="text-sm text-muted-foreground prose prose-sm dark:prose-invert max-w-none" 
                      dangerouslySetInnerHTML={{ __html: entry.commonPitfalls }} 
                    />
                  </CardContent>
                </Card>
              )}
            </section>
          )}

          {entry.extendedContent && (
            <section>
              <Separator className="mb-6" />
              <div 
                className="prose prose-lg dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: entry.extendedContent }}
              />
            </section>
          )}

          {sponsorship && sponsorship.ctaLabel && sponsorship.ctaUrl && (
            <section>
              <Separator className="mb-6" />
              <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-6">
                    {sponsorship.companyLogo && (
                      <img 
                        src={sponsorship.companyLogo} 
                        alt={sponsorship.companyName || "Sponsor"} 
                        className="h-16 w-16 object-contain rounded-lg bg-background p-2"
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{sponsorship.companyName}</p>
                      <p className="text-muted-foreground">
                        {sponsorship.ctaType === "enquiry" 
                          ? "Request more information about this topic" 
                          : "Learn more from our knowledge partner"}
                      </p>
                    </div>
                    <Button 
                      onClick={() => window.open(sponsorship.ctaUrl!, "_blank", "noopener,noreferrer")}
                      size="lg"
                      className="gap-2"
                      data-testid="button-sponsor-cta"
                    >
                      {sponsorship.ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {relatedEntries.length > 0 && (
            <section>
              <Separator className="mb-6" />
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Related Terms
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {relatedEntries.map((relEntry) => {
                  const relConfig = relEntry.keywordType 
                    ? KEYWORD_TYPE_CONFIG[relEntry.keywordType] || KEYWORD_TYPE_CONFIG.technical_term
                    : KEYWORD_TYPE_CONFIG.technical_term;
                  const RelIcon = relConfig.icon;
                  
                  return (
                    <Card 
                      key={relEntry.id} 
                      className="cursor-pointer hover-elevate"
                      onClick={() => setLocation(`/glossary/${relEntry.slug || relEntry.normalizedTerm}`)}
                      data-testid={`card-related-${relEntry.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded ${relConfig.bgColor}`}>
                            <RelIcon className={`h-4 w-4 ${relConfig.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{relEntry.title || relEntry.term}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {relEntry.definition}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {externalLinks.length > 0 && (
            <section>
              <Separator className="mb-6" />
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <ExternalLink className="h-5 w-5" />
                Learn More
              </h2>
              <div className="flex flex-wrap gap-3">
                {externalLinks.map((link, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(link.url, "_blank", "noopener,noreferrer")}
                    className="gap-2"
                    data-testid={`link-external-${i}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {link.label}
                  </Button>
                ))}
              </div>
            </section>
          )}

          <footer className="pt-8 border-t">
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setLocation("/glossary")}
                data-testid="link-browse-all"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Browse All Terms
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" data-testid="button-share">
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" data-testid="button-bookmark">
                  <Bookmark className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </footer>
        </article>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTerm",
          "name": entry.title || entry.term,
          "description": entry.definition,
          "inDefinedTermSet": {
            "@type": "DefinedTermSet",
            "name": "FM World Glossary"
          }
        })
      }} />
      <SiteFooter />
    </div>
  );
}
